/**
 * Canonical course-offering assessment roster — shared by the course Assessments
 * tab (board/list), assessment detail record switcher, and lifecycle helpers.
 */

import { clampAssessmentMarks } from "@/lib/assessment-marks"
import { readCreatedAssessments } from "@/lib/created-assessments"
import { isAssessmentDeleted } from "@/lib/delete-assessment"
import type { Assessment } from "@/lib/mock/exam-data"

export type CourseAssessmentCounts = {
  draft: number
  scheduled: number
  live: number
  evaluating: number
  resultsPublished: number
}

export type CourseAssessmentStatus = Assessment["status"]

export const COURSE_ASSESSMENT_TYPES = ["Exam", "Quiz", "Practical"] as const

export type CourseAssessmentType = (typeof COURSE_ASSESSMENT_TYPES)[number]

export function isCourseAssessmentType(
  value: unknown,
): value is CourseAssessmentType {
  return (
    typeof value === "string" &&
    (COURSE_ASSESSMENT_TYPES as readonly string[]).includes(value)
  )
}

export function assessmentTypeFromTitle(title: string): CourseAssessmentType {
  const lower = title.toLowerCase()
  if (lower.includes("quiz")) return "Quiz"
  if (lower.includes("practical")) return "Practical"
  return "Exam"
}

export interface CourseAssessment {
  id: string
  code: string
  name: string
  type: CourseAssessmentType
  status: CourseAssessmentStatus
  questionCount: number
  totalMarks: number
  duration: number
  startDate: string
  endDate: string
  submissionsCompleted?: number
  totalEnrolled?: number
  evaluatingSince?: string
  averageScore?: number
}

export interface CourseAssessmentPeer {
  id: string
  code: string
  title: string
  courseCode: string
  status: CourseAssessmentStatus
  startDate: string
  endDate: string
  questionCount: number
  type: CourseAssessmentType
}

type CourseAssessmentSeed = Omit<CourseAssessment, "id" | "code" | "name"> & {
  slug: string
  shortName: string
}

/**
 * Same roster on every course offering detail demo (NURS-210 is the reference).
 * Draft slugs intentionally cover different author states — see
 * `assessment-detail-paper.ts` for paper seeds per slug.
 */
const COURSE_OFFERING_ASSESSMENT_SEEDS: readonly CourseAssessmentSeed[] = [
  {
    slug: "a1",
    shortName: "Unit Exam",
    type: "Exam",
    status: "Evaluating",
    questionCount: 40,
    totalMarks: 100,
    duration: 120,
    startDate: "2026-06-01",
    endDate: "2026-06-02",
    submissionsCompleted: 42,
    totalEnrolled: 45,
    evaluatingSince: "3 days ago",
  },
  {
    slug: "a2",
    shortName: "Medication Quiz",
    type: "Quiz",
    status: "Results Published",
    questionCount: 10,
    totalMarks: 20,
    duration: 15,
    startDate: "2026-06-05",
    endDate: "2026-06-05",
    submissionsCompleted: 45,
    totalEnrolled: 45,
    averageScore: 88,
  },
  {
    slug: "a3",
    shortName: "Skills Check",
    type: "Practical",
    status: "Evaluating",
    questionCount: 8,
    totalMarks: 50,
    duration: 90,
    startDate: "2026-06-12",
    endDate: "2026-06-14",
    evaluatingSince: "2 days ago",
  },
  {
    slug: "a4",
    shortName: "Cumulative Final",
    type: "Exam",
    status: "Live",
    questionCount: 60,
    totalMarks: 100,
    duration: 180,
    startDate: "2026-06-17",
    endDate: "2026-06-19",
    submissionsCompleted: 25,
    totalEnrolled: 45,
  },
  {
    slug: "a5",
    shortName: "Case Study Draft",
    type: "Exam",
    status: "Draft",
    questionCount: 20,
    totalMarks: 100,
    duration: 0,
    startDate: "2026-06-20",
    endDate: "2026-06-22",
  },
  {
    slug: "a6",
    shortName: "Make-up Exam",
    type: "Exam",
    status: "Scheduled",
    questionCount: 35,
    totalMarks: 100,
    duration: 120,
    startDate: "2026-06-24",
    endDate: "2026-06-25",
  },
  {
    slug: "a7",
    shortName: "Extra Credit Quiz",
    type: "Quiz",
    status: "Draft",
    questionCount: 15,
    totalMarks: 25,
    duration: 20,
    startDate: "2026-05-28",
    endDate: "2026-05-28",
  },
  {
    slug: "a8",
    shortName: "Final Preview",
    type: "Exam",
    status: "Draft",
    questionCount: 25,
    totalMarks: 100,
    duration: 120,
    startDate: "2026-06-26",
    endDate: "2026-06-28",
  },
  {
    slug: "a9",
    shortName: "Capstone Proposal",
    type: "Exam",
    status: "Draft",
    // Planned exam size. The paper itself stays empty (`paperForAssessment`).
    questionCount: 20,
    totalMarks: 100,
    duration: 90,
    startDate: "2026-07-01",
    endDate: "2026-07-15",
  },
] as const

export function buildCourseOfferingAssessments(courseCode: string): CourseAssessment[] {
  const roster = COURSE_OFFERING_ASSESSMENT_SEEDS.map((seed, idx) => ({
    id: `${courseCode}-${seed.slug}`,
    code: `ASM-${201 + idx}`,
    name: `${courseCode} ${seed.shortName}`,
    type: seed.type,
    status: seed.status,
    questionCount: seed.questionCount,
    totalMarks: clampAssessmentMarks(seed.totalMarks),
    duration: seed.duration,
    startDate: seed.startDate,
    endDate: seed.endDate,
    submissionsCompleted: seed.submissionsCompleted,
    totalEnrolled: seed.totalEnrolled,
    evaluatingSince: seed.evaluatingSince,
    averageScore: seed.averageScore,
  })).filter((row) => !isAssessmentDeleted(row.id))

  const knownIds = new Set(roster.map((row) => row.id))
  for (const row of readCreatedAssessments()) {
    if (row.course !== courseCode || knownIds.has(row.id) || isAssessmentDeleted(row.id)) {
      continue
    }
    roster.push({
      id: row.id,
      code: row.code,
      name: row.title,
      type: isCourseAssessmentType(row.type)
        ? row.type
        : assessmentTypeFromTitle(row.title),
      status: row.status,
      questionCount: row.questionCount,
      totalMarks: clampAssessmentMarks(row.totalMarks ?? 100),
      duration: 0,
      startDate: row.dueDate,
      endDate: row.dueDate,
    })
    knownIds.add(row.id)
  }

  return roster
}

/** Record switcher + assessment-details resolve path for `-a` ids. */
export function courseOfferingAssessmentPeers(courseCode: string): CourseAssessmentPeer[] {
  return buildCourseOfferingAssessments(courseCode).map(row => ({
    id: row.id,
    code: row.code,
    title: row.name,
    courseCode,
    status: row.status,
    startDate: row.startDate,
    endDate: row.endDate,
    questionCount: row.questionCount,
    type: row.type,
  }))
}

export function findCourseOfferingAssessment(
  assessmentId: string,
): CourseAssessment | undefined {
  const match = assessmentId.match(/^(.+)-a(\d+)$/)
  if (!match) return undefined
  const courseCode = match[1]!
  return buildCourseOfferingAssessments(courseCode).find(row => row.id === assessmentId)
}

export function countCourseAssessmentsByStatus(
  assessments: readonly CourseAssessment[],
): CourseAssessmentCounts {
  const counts = {
    draft: 0,
    scheduled: 0,
    live: 0,
    evaluating: 0,
    resultsPublished: 0,
  }
  for (const row of assessments) {
    switch (row.status) {
      case "Draft":
        counts.draft += 1
        break
      case "Scheduled":
        counts.scheduled += 1
        break
      case "Live":
        counts.live += 1
        break
      case "Evaluating":
        counts.evaluating += 1
        break
      case "Results Published":
        counts.resultsPublished += 1
        break
      default:
        break
    }
  }
  return counts
}

/** Slug → lifecycle for `resolveAssessmentLifecycleStatus`. */
export function offeringAssessmentStatusBySlug(): Record<string, CourseAssessmentStatus> {
  return Object.fromEntries(
    COURSE_OFFERING_ASSESSMENT_SEEDS.map(seed => [seed.slug, seed.status]),
  )
}
